<template>
  <div v-if="visible" class="fu-uitoggle" :class="`fu-uitoggle--${target}`">
    <span class="fu-uitoggle__note">{{ note }}</span>
    <button type="button" class="fu-uitoggle__btn" data-test="fu-ui-toggle" :data-target="target" @click="$emit('toggle', target)">
      {{ buttonLabel }}
    </button>
  </div>
</template>

<script>
import { stageOf, CLASSIC_UNTIL } from '@/utils/followups/uiVersion.js';
import { formatClassicUntil, officeSwitchLabel } from '@/utils/followups/uiCopy.js';

/**
 * The link between a page's old and new UI (uiVersion.js). `target` is the UI the link goes TO: 'old'
 * ("Classic view" / "Switch to classic Teamio", shown on the new page) or 'new' ("New view" / "Try the
 * new Teamio", shown on the old one). Emits toggle(target); the page hands it to switchUi()
 * (uiSwitch.js), which stores the choice and — for an office user — reloads.
 *
 * It decides by ROLE itself, so a page renders it unconditionally:
 *   • Super Admin (Teamio staff): exactly the original wording, per-area switch — `noteText` honoured.
 *   • Office user: only while this area's stage (`pageKey`) is 'invite' or 'default', and only for a
 *     signed-in employee (the choice is stored per employee ID). Its own wording, `noteText` ignored.
 *     No `pageKey` ⇒ hidden for an office user (the classic Patient Profile's section toggles).
 *   • Hidden for EVERYONE once the area's stage is 'retired' (the new page is the only page).
 *   • Hidden in a demo session (customer/isDemo): the demo shows the new Teamio only.
 */
export default {
  name: 'FollowupsUiToggle',
  props: {
    target: {
      type: String,
      required: true,
      validator: v => v === 'old' || v === 'new',
    },
    // The area (uiVersion.js PAGE_KEYS) whose stage decides whether an office user sees this link.
    pageKey: { type: String, default: '' },
    // Optional wording for a SECTION rather than a page (Patient Profile's Follow-ups, Phase 5).
    // Super Admins only — an office user always reads the office wording.
    noteText: { type: String, default: '' },
  },
  computed: {
    // Demo session (customer/isDemo): no classic-Teamio prompts, the demo shows the new Teamio only (Saumil 8a).
    // Store-safe: component tests mount without a store or without getters.
    viewerIsDemo() {
      const store = this.$store;
      return !!(store && store.getters && store.getters['customer/isDemo'] === true);
    },
    customerState() {
      const store = this.$store;
      return (store && store.state && store.state.customer) || {};
    },
    // Not named isSuperAdmin / userID: src/mixin.js computeds of those names would be shadowed.
    viewerIsSuperAdmin() {
      const user = this.customerState.user;
      return !!(user && user.Role === 'Super Admin');
    },
    viewerKey() {
      const id = this.customerState.userID;
      return id === null || id === undefined ? '' : String(id).trim();
    },
    visible() {
      if (this.viewerIsDemo === true) return false; // the demo shows the new Teamio only (Saumil 8a)
      const stage = stageOf(this.pageKey);
      // 'retired': the new page is the only page for everyone (shouldUseNewFollowupsUi answers true whatever
      // the Super Admin's own key says), so a link back would do nothing. No pageKey ⇒ stage null ⇒ unaffected.
      if (stage === 'retired') return false;
      if (this.viewerIsSuperAdmin) return true;
      return this.viewerKey !== '' && (stage === 'invite' || stage === 'default');
    },
    note() {
      if (this.viewerIsSuperAdmin) {
        if (this.noteText) return this.noteText;
        return this.target === 'old'
          ? 'Super Admin preview — offices still see the classic page.'
          : 'Super Admin: a redesigned version of this page is ready to check.';
      }
      return this.target === 'old'
        ? `Prefer the old layout? Classic Teamio is available until ${formatClassicUntil(CLASSIC_UNTIL)}.`
        : 'A new version of this page is ready.';
    },
    buttonLabel() {
      if (this.viewerIsSuperAdmin) return this.target === 'old' ? 'Classic view' : 'New view';
      return officeSwitchLabel(this.target);
    },
  },
};
</script>

<style lang="scss" scoped>
@import './mixins';

.fu-uitoggle {
  @include fu-font-base;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 4px 10px;
  margin-bottom: $fu-space-2;
  font-size: 12.5px;
}

.fu-uitoggle__note {
  color: $fu-text-muted;
}

.fu-uitoggle__btn {
  @include fu-link-btn;
  font-size: 12.5px;
  padding: 2px 0;
}
</style>
